import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Checkbox, Chip, CircularProgress, TextField, Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

const useStyles = makeStyles((theme) => ({
  container: { marginBottom: theme.spacing(0.5) },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f5f5f5',
      minHeight: 40,
    },
  },
  checkboxIcon: {
    marginRight: theme.spacing(1),
    color: '#9e9e9e',
  },
  checkboxCheckedIcon: {
    marginRight: theme.spacing(1),
    color: '#3f51b5',
  },
  chip: {
    backgroundColor: '#e8eaf6',
    color: '#3f51b5',
    fontWeight: 500,
    '& .MuiChip-deleteIcon': {
      color: '#7986cb',
      '&:hover': { color: '#3f51b5' },
    },
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '12px',
    marginTop: theme.spacing(0.5),
  },
}));

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Generic searchable multi-select that stores the option id as the value.
 * Any previously-saved ids that no longer exist in `options` are preserved as
 * synthetic options so legacy / unknown data stays visible and removable.
 */
const OptionMultiSelect = ({
  label,
  placeholder = 'Search...',
  options = [],
  values = [],
  onChange,
  loading = false,
  error,
  emptyText,
  loadingText = 'Loading...',
  errorText = 'Failed to load options. Showing previously saved values only.',
}) => {
  const classes = useStyles();

  const mergedOptions = useMemo(() => {
    const byId = new Map();
    (options || []).forEach((opt) => {
      if (opt && opt.id != null) {
        byId.set(String(opt.id), { id: opt.id, label: opt.label ?? '' });
      }
    });
    (values || []).forEach((v) => {
      if (v == null || v === '') return;
      const key = String(v);
      if (!byId.has(key)) byId.set(key, { id: v, label: String(v) });
    });
    return Array.from(byId.values()).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [options, values]);

  const selectedOptions = useMemo(() => {
    const selectedKeys = new Set((values || []).map((v) => String(v)));
    return mergedOptions.filter((o) => selectedKeys.has(String(o.id)));
  }, [mergedOptions, values]);

  const handleChange = (_, next) => onChange(next.map((o) => o.id));

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>{label}</Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={mergedOptions}
        value={selectedOptions}
        loading={loading}
        noOptionsText={loading ? loadingText : (emptyText || 'No options found')}
        onChange={handleChange}
        getOptionLabel={(option) => option.label || ''}
        getOptionSelected={(option, value) => String(option.id) === String(value.id)}
        className={classes.autocomplete}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              size="small"
              className={selected ? classes.checkboxCheckedIcon : classes.checkboxIcon}
            />
            {option.label}
          </>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              key={String(option.id)}
              label={option.label}
              size="small"
              className={classes.chip}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && (
        <Typography className={classes.errorText}>{errorText}</Typography>
      )}
    </Box>
  );
};

export default OptionMultiSelect;
